"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import {
  BrainCircuit,
  ClipboardList,
  Cpu,
  Clock,
  Rocket,
  TrendingUp,
  ShieldAlert,
  Star,
} from "lucide-react"
import { useCases } from "@/lib/mock-data"
import { ClassificationBadge, FeasibilityBadge, PriorityBadge, StatusBadge } from "@/components/status-badges"
import type { AIUseCase } from "@/lib/types"

interface DashboardSummary {
  totalUseCases: number
  submittedPainPoints: number
  aiMapped: number
  pendingFeasibility: number
  approvedForDiscovery: number
  highPriorityUseCases: number
  l1DataUseCases: number
  averagePriorityScore: number
}

function KpiCard({
  title,
  value,
  icon: Icon,
  description,
  accent,
}: {
  title: string
  value: number | string
  icon: React.ElementType
  description?: string
  accent?: string
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{title}</span>
            <span className="text-2xl font-bold text-foreground">{value}</span>
            {description && <span className="text-xs text-muted-foreground">{description}</span>}
          </div>
          <div className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${accent ?? "bg-muted"}`}>
            <Icon className="size-4 text-foreground/70" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function BarChart({
  data,
  colorClass,
}: {
  data: { label: string; count: number; color?: string }[]
  colorClass?: string
}) {
  const max = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex flex-col gap-2.5">
      {data.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-28 shrink-0 truncate">{item.label}</span>
          <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden">
            <div
              className={`h-full rounded-sm transition-all ${item.color ?? colorClass ?? "bg-primary/70"}`}
              style={{ width: `${(item.count / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-foreground w-5 text-right shrink-0">{item.count}</span>
        </div>
      ))}
    </div>
  )
}

export function DashboardContent() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch("http://95.217.189.103:5000/api/dashboard/summary")
      .then((r) => r.json())
      .then((data) => {
        setSummary(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // Charts data
  const byDept = Object.entries(
    useCases.reduce<Record<string, number>>((acc, uc) => {
      acc[uc.department] = (acc[uc.department] || 0) + 1
      return acc
    }, {})
  ).map(([label, count]) => ({ label, count }))

  const byFeasibility = [
    { label: "Green", count: useCases.filter((u) => u.feasibilityFlag === "Green").length, color: "bg-emerald-500" },
    { label: "Amber", count: useCases.filter((u) => u.feasibilityFlag === "Amber").length, color: "bg-amber-500" },
    { label: "Red", count: useCases.filter((u) => u.feasibilityFlag === "Red").length, color: "bg-red-500" },
  ]

  const byClassification = [
    { label: "Public", count: useCases.filter((u) => u.dataClassification === "Public").length, color: "bg-emerald-500" },
    { label: "L0 — Internal", count: useCases.filter((u) => u.dataClassification === "L0").length, color: "bg-blue-500" },
    { label: "L1 — Confidential", count: useCases.filter((u) => u.dataClassification === "L1").length, color: "bg-orange-500" },
  ]

  const statusGroups = [
    "Draft", "Submitted", "AI Mapped", "Feasibility Pending",
    "Feasibility Completed", "Prioritized", "Approved for Discovery",
  ]
  const byStatus = statusGroups
    .map((label) => ({ label, count: useCases.filter((u) => u.status === label).length }))
    .filter((d) => d.count > 0)

  const kpiCards = loading
    ? null
    : [
        { title: "Total Use Cases", value: summary?.totalUseCases ?? 0, icon: BrainCircuit, accent: "bg-blue-100" },
        { title: "Submitted Pain Points", value: summary?.submittedPainPoints ?? 0, icon: ClipboardList, accent: "bg-violet-100" },
        { title: "AI Mapped", value: summary?.aiMapped ?? 0, icon: Cpu, accent: "bg-cyan-100" },
        { title: "Pending Feasibility", value: summary?.pendingFeasibility ?? 0, icon: Clock, accent: "bg-amber-100" },
        { title: "Approved for Discovery", value: summary?.approvedForDiscovery ?? 0, icon: Rocket, accent: "bg-emerald-100" },
        { title: "High Priority (P1)", value: summary?.highPriorityUseCases ?? 0, icon: TrendingUp, accent: "bg-red-100" },
        { title: "L1 Data Use Cases", value: summary?.l1DataUseCases ?? 0, icon: ShieldAlert, accent: "bg-orange-100" },
        { title: "Avg Priority Score", value: summary?.averagePriorityScore ?? 0, icon: Star, accent: "bg-yellow-100" },
      ]

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* KPIs */}
      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Key Metrics
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-5 pb-5">
                  <Skeleton className="h-6 w-20 mb-2" />
                  <Skeleton className="h-8 w-12" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpiCards!.map((card) => (
              <KpiCard key={card.title} {...card} />
            ))}
          </div>
        )}
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">By Department</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart data={byDept} colorClass="bg-blue-500" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">By Feasibility</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart data={byFeasibility} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">By Data Classification</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart data={byClassification} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">By Status</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart data={byStatus} colorClass="bg-violet-500" />
          </CardContent>
        </Card>
      </section>

      {/* Recent Activity */}
      <section>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Recent Activity</CardTitle>
            <CardDescription className="text-xs">Latest use cases and updates across all departments</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3">Use Case Name</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Department</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Owner</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Feasibility</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Score</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden xl:table-cell">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {useCases.map((uc: AIUseCase, i: number) => (
                    <tr key={uc.id} className={`border-b border-border last:border-0 ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                      <td className="px-6 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-foreground text-xs">{uc.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{uc.id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground">{uc.department}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-muted-foreground">{uc.businessOwner}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge value={uc.status} />
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <FeasibilityBadge value={uc.feasibilityFlag} />
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-foreground">{uc.priorityScore}</span>
                          <PriorityBadge value={uc.priorityBand} />
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <span className="text-xs text-muted-foreground">{uc.updatedAt}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
